import { useEffect, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import Container from '@components/Container';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import Button from '@components/Button';
import ShareWidget from '@components/ShareWidget';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import { fetchBlogPostBySlug, type BlogPost } from '@lib/api';

function formatDate(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), 'iframe'],
  attributes: {
    ...defaultSchema.attributes,
    iframe: [
      ...(defaultSchema.attributes?.iframe || []),
      'src',
      'width',
      'height',
      'frameborder',
      'allow',
      'allowfullscreen',
      'title',
      'loading',
      'referrerpolicy',
    ],
  },
};

function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const allowDrafts = searchParams.get('preview') === '1';
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { role } = useUserRole(user?.id ?? null);
  const canEdit = role === 'admin';
  const navigate = useNavigate();
  const launchDashboard = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const defaultTitle = 'Xhalin Stories';
    const pageTitle = post?.title
      ? `${post.title} | ${defaultTitle}`
      : defaultTitle;
    document.title = pageTitle;

    const description =
      post?.excerpt ||
      post?.content?.slice(0, 160) ||
      'Guided breathwork stories from Xhalin.';
    const imageUrl = post?.cover_image || '/pictures/logo-v4.png';

    const metaSpecs: Array<{
      attr: 'name' | 'property';
      key: string;
      content: string;
    }> = [
      { attr: 'name', key: 'description', content: description },
      { attr: 'property', key: 'og:title', content: pageTitle },
      { attr: 'name', key: 'twitter:title', content: pageTitle },
      { attr: 'property', key: 'og:image', content: imageUrl },
      { attr: 'name', key: 'twitter:image', content: imageUrl },
      { attr: 'property', key: 'og:description', content: description },
    ];

    metaSpecs.forEach(({ attr, key, content }) => {
      let tag = document.head.querySelector(
        `meta[${attr}="${key}"]`
      ) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });

    return () => {
      document.title = 'Xhalin';
    };
  }, [post]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!slug) {
        setError('Missing blog post slug.');
        setLoading(false);
        return;
      }
      setError(null);
      setLoading(true);
      try {
        const data = await fetchBlogPostBySlug(slug, allowDrafts);
        if (!active) return;
        if (!data) {
          setError('This story is not available.');
          setPost(null);
        } else {
          setPost(data);
        }
      } catch (err) {
        if (!active) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load this story right now.'
        );
        setPost(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [slug, allowDrafts]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-emerald-50">
      <Container>
        <div className="py-12">
          <div className="mb-6 flex items-center justify-between gap-4">
            <Link
              to="/blog"
              className="inline-flex items-center rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              {'< Back to Blog'}
            </Link>
            {canEdit && post && (
              <Link
                to={`/admin/blog/${post.id}/edit`}
                className="item-selected inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-100"
              >
                Edit post
              </Link>
            )}
          </div>

          {loading ? (
            <div className="space-y-4">
              {[0, 1, 2].map((sk) => (
                <div
                  key={sk}
                  className="h-6 animate-pulse rounded-2xl bg-gray-200/70"
                />
              ))}
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          ) : post ? (
            <>
              <ShareWidget
                url={typeof window !== 'undefined' ? window.location.href : ''}
                title={post.title}
                excerpt={post.excerpt}
              />
              <article className="rounded-3xl bg-white/90 p-8 shadow-xl backdrop-blur">
                <header className="mb-8">
                  <p className="text-sm uppercase tracking-[0.3em] text-indigo-500">
                    Xhalin Stories
                  </p>
                  <h1 className="mt-3 text-4xl font-bold text-gray-900">
                    {post.title}
                  </h1>
                  <div className="mt-2 text-sm text-gray-500">
                    {formatDate(post.published_at) ?? 'Unpublished draft'}
                  </div>
                  {post.cover_image ? (
                    <div className="mt-6 overflow-hidden">
                      <div className="m-0 mx-auto block max-w-[90vw] rounded-2xl bg-gradient-to-b from-indigo-300 via-indigo-100 to-emerald-300 p-[5px] sm:max-w-[800px]">
                        <div className="flex h-[180px] items-center justify-center overflow-hidden rounded-xl bg-transparent sm:h-[360px] md:h-[420px]">
                          <img
                            src={post.cover_image}
                            alt={post.title}
                            className="max-h-full max-w-full object-contain object-center"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </header>
                <div className="prose prose-indigo max-w-none space-y-6 leading-relaxed text-gray-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[
                      rehypeRaw,
                      [rehypeSanitize, sanitizeSchema],
                    ]}
                  >
                    {post.content}
                  </ReactMarkdown>
                </div>
                <div className="mt-10 rounded-2xl border border-indigo-100 bg-indigo-50/70 p-6 text-center">
                  <p className="mb-4 text-sm text-gray-700">
                    Ready to breathe better? Launch the{' '}
                    <span className="logo-font font-bold">Xhalin</span>{' '}
                    Dashboard and
                  </p>
                  <Button
                    type="button"
                    size="lg"
                    variant="primary"
                    className="begin-breathing-button w-full sm:w-auto"
                    onClick={launchDashboard}
                  >
                    Begin Breathing
                  </Button>
                </div>
                <ShareWidget
                  url={
                    typeof window !== 'undefined' ? window.location.href : ''
                  }
                  title={post.title}
                  excerpt={post.excerpt}
                  mobile
                />
              </article>
            </>
          ) : null}
        </div>
      </Container>
    </div>
  );
}

export default BlogPost;
